import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import * as LaunchDarklyClient from 'launchdarkly-js-client-sdk';
import {
  BehaviorSubject,
  Observable,
  catchError, first, from,
  map, of,
  tap
} from 'rxjs';

import { isPlatformServer } from '@angular/common';
import { ConfigurationService, ILaunchDarklyAppConfig } from '@sondermind/configuration';
import {
  ILaunchDarklyMetricContext,
  ILaunchDarklyService,
  ILaunchDarklyUser,
  LDBooleanFeatureFlags,
  LDJsonFeatureFlags,
  LDNumberFeatureFlags,
  LDStringFeatureFlags,
  LaunchDarklyMetricEvent
} from '@sondermind/launch-darkly';

@Injectable()
export class LaunchDarklyService implements ILaunchDarklyService {
  client: LaunchDarklyClient.LDClient | undefined;
  globalClient: LaunchDarklyClient.LDClient | undefined;
  clientLoaded: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
  globalClientLoaded: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
  errorOnInit: boolean = false;
  ldFlagSubject: BehaviorSubject<LaunchDarklyClient.LDFlagSet> = new BehaviorSubject<LaunchDarklyClient.LDFlagSet>({});
  ldFlag$: Observable<LaunchDarklyClient.LDFlagSet> = this.ldFlagSubject.asObservable();

  constructor(
    private configurationService: ConfigurationService<ILaunchDarklyAppConfig>,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  /**
   * If a LaunchDarkly client connection doesn't exist, open one.
   * We receive a user object in the shape of an ILaunchDarklyUser and send that data on client connection init.
   * Check connection to LaunchDarkly, but fail silently if connection fails.
   * We don't want LD issues to prevent any portal functionality.
   * When connection succeeds, check state of feature flags.
   */
  initClient(user: ILaunchDarklyUser, params: { useGlobalClient?: boolean; forceInit?: boolean } = {}): void {
    // We should only initialize a connection if one doesn't exist or it errored previously.
    const clientProp: 'client' | 'globalClient' = params.useGlobalClient ? 'globalClient' : 'client';
    if (!this[clientProp] || this.errorOnInit || params.forceInit) {
      if (this.errorOnInit) {
        this.errorOnInit = false;
        this.closeClient();
      }

      const launchDarklyClientSdkId = this.configurationService.env.app?.launchDarklyClientSideId;

      if (launchDarklyClientSdkId) {
        let options = {};
        if (!!this.configurationService.env.dataDogApplicationID &&
          !!this.configurationService.env.dataDogClientToken &&
          this.configurationService.env.enableDataDogSessionReplay) {
          // Set feature flag tracking for datadog if we have enabled session replay
          options = {
            inspectors: [
              {
                type: 'flag-used',
                name: 'dd-inspector',
                method: (key: string, detail: LaunchDarklyClient.LDEvaluationDetail) => {
                  datadogRum.addFeatureFlagEvaluation(key, detail.value);
                },
              },
            ],
          };
        }

        options['allAttributesPrivate'] = true;

        this[clientProp] = LaunchDarklyClient.initialize(launchDarklyClientSdkId, user, options);

        // Check that initialization succeeds.
        from(this[clientProp].waitForInitialization()).pipe(
          catchError(() => {
            params.useGlobalClient ? this.globalClientLoaded.next(false) : this.clientLoaded.next(false);
            this.errorOnInit = true;
            return of(undefined);
          }),
          tap(() => {
            params.useGlobalClient ? this.globalClientLoaded.next(true) : this.clientLoaded.next(true);
          })
        ).subscribe();
      }
    }
  }

  /**
   * This function will watch for live changes. This will automatically update the LD cache to get the newest value.
   * @param flagName
   */
  watchFlagForChanges(flagName: string): void {
    this.client?.on(`change:${flagName}`, (val, prev) => { });
  }

  /**
   * Send a custom metric event to the LaunchDarkly client.
   * Do not attempt to send if there was an errorOnInit.
   * Usage of ? is probably overly cautious, but in case of a timing issue with initialization.
   *
   * additionalData is optional and used for more context around an event if needed.
   */
  sendMetricEvent(ldMetric: LaunchDarklyMetricEvent, additionalData: ILaunchDarklyMetricContext = {}): void {
    if (!this.errorOnInit) {
      this.client?.track(ldMetric, additionalData);
    }
  }

  // Check if a boolean-based Feature Flag is on.
  checkFlagEnabled$(flag: LDBooleanFeatureFlags): Observable<boolean> {
    if (isPlatformServer(this.platformId)) return of(false);
    return this.clientLoaded.pipe(first((loaded) => loaded !== undefined),
      map((loaded) => {
        if (!loaded) return false;
        const flagValue = this.client?.variation(flag, false);
        // We can't guarantee LaunchDarkly will send a boolean down
        if (typeof flagValue === 'boolean') {
          return flagValue;
        }
        return false;
      }));
  }

  // Check if a boolean-based Feature Flag is on.
  globalCheckFlagEnabled$(flag: LDBooleanFeatureFlags): Observable<boolean> {
    if (isPlatformServer(this.platformId)) return of(false);
    return this.globalClientLoaded.pipe(first((loaded) => loaded !== undefined),
      map((loaded) => {
        if (!loaded) return false;
        const flagValue = this.globalClient?.variation(flag, false);
        // We can't guarantee LaunchDarkly will send a boolean down
        if (typeof flagValue === 'boolean') {
          return flagValue;
        }
        return false;
      }));
  }

  /**
   * Get the value of a number-based Feature Flag.
   */
  checkNumberFlagValue$(flag: LDNumberFeatureFlags, defaultFlag: number = 0): Observable<number> {
    if (isPlatformServer(this.platformId)) return of(0);
    return this.clientLoaded.pipe(first((loaded) => loaded !== undefined),
      map((loaded) => {
        if (!loaded) return defaultFlag;
        const flagValue = this.client?.variation(flag, defaultFlag);
        // We can't guarantee LaunchDarkly will send a number down
        if (typeof flagValue === 'number') {
          return flagValue;
        }
        return defaultFlag;
      }));
  }

  /**
   * Get the value of a string-based Feature Flag.
   */
  checkFlagValue$(flag: LDStringFeatureFlags, defaultFlag: string = ''): Observable<string> {
    if (isPlatformServer(this.platformId)) return of('');
    return this.clientLoaded.pipe(first((loaded) => loaded !== undefined),
      map((loaded) => {
        if (!loaded) return defaultFlag;
        const flagValue = this.client?.variation(flag, defaultFlag);
        // We can't guarantee LaunchDarkly will send a string down
        if (typeof flagValue === 'string') {
          return flagValue;
        }
        return defaultFlag;
      }));
  }

  /**
   * Get the value of a JSON-based Feature Flag.
   */
  checkFlagJsonValue$<T>(flag: LDJsonFeatureFlags): Observable<T | null> {
    if (isPlatformServer(this.platformId)) return of(null);
    return this.clientLoaded.pipe(first((loaded) => loaded !== undefined),
      map((loaded) => {
        if (!loaded) return null;

        const flagValue = this.client?.variation(flag, null) as T;

        if (typeof flagValue === 'object') {
          return flagValue;
        }

        return null;
      }));
  }

  /**
   * Get all flags active for a user
   */
  getAllFlags$(): Observable<LaunchDarklyClient.LDFlagSet> {
    return this.clientLoaded.pipe(first((loaded) => loaded !== undefined),
      map((loaded) => {
        if (!loaded) return null;

        return this.client.allFlags();
      }));
  }

  /**
   * Used to close the LDClient connection when the AppComponent is destroyed.
   */
  closeClient(): void {
    this.client?.close();
  }

  /**
   * @deprecated use `checkFlag` instead
   * This method will not work on page refresh without calling `waitForInitialization` first.
   */
  isFlagEnabled(flag: LDBooleanFeatureFlags): boolean {
    if (this.errorOnInit || !this.client) { return false }

    const flagValue = this.client.variation(flag, false);

    // We can't guarantee LaunchDarkly will send a boolean down
    if (typeof flagValue === 'boolean') {
      return flagValue;
    }
    return false;
  }

  /**
   * @deprecated use `checkFlagJsonValue` instead
   * This method will not work on page refresh without calling `waitForInitialization` first.
   */
  getFlagJsonValue<T>(flag: LDJsonFeatureFlags): T | null {
    if (!this.client) { return null }

    const flagValue = this.client.variation(flag, null) as T;

    if (typeof flagValue === 'object') {
      return flagValue;
    }
    return null;
  }

  /**
   * @deprecated use `checkFlagValue` instead
   * This method will not work on page refresh without calling `waitForInitialization` first.
   */
  getFlagValue(flag: LDStringFeatureFlags, defaultFlag: string = ''): string {
    if (this.errorOnInit || !this.client) { return defaultFlag }

    const flagValue = this.client.variation(flag, defaultFlag);

    // We can't guarantee LaunchDarkly will send a string down
    if (typeof flagValue === 'string') {
      return flagValue;
    }
    return defaultFlag;
  }

  initGlobalLaunchDarklyClient() {
    this.initClient(this.buildNewLaunchDarklyClient(), { useGlobalClient: true });
  }

  private buildNewLaunchDarklyClient(): ILaunchDarklyUser {
    return {
      kind: 'anonUserId',
      anonymous: true,
    };
  }
}
